import { useEffect, useRef, useState } from "react";
import { PDFDocument } from "pdf-lib";
import { Modal } from "../../app/Modal";
import { IconCamera, IconChevronLeft, IconChevronRight, IconTrash } from "../../app/icons";

/** A4 in punten; foto's worden passend op een staande of liggende pagina gezet. */
const A4 = { w: 595.28, h: 841.89 };
const MAX_PX = 2000;

interface Page {
  key: string;
  /** Verkleinde JPEG, al bewerkt (zwart-wit of kleur). */
  blob: Blob;
  url: string;
  width: number;
  height: number;
}

/** Foto verkleinen en eventueel als scan bewerken: grijs met meer contrast, papier wordt wit. */
async function processPhoto(file: File, scan: boolean): Promise<Omit<Page, "key" | "url">> {
  const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  const f = Math.min(1, MAX_PX / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * f);
  const h = Math.round(bmp.height * f);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  if (scan) {
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const y = 0.299 * d[i]! + 0.587 * d[i + 1]! + 0.114 * d[i + 2]!;
      // Contrast omhoog rond 50 % en lichte tinten naar wit (papier).
      const v = Math.max(0, Math.min(255, (y - 128) * 1.6 + 150));
      d[i] = d[i + 1] = d[i + 2] = v > 225 ? 255 : v;
    }
    ctx.putImageData(img, 0, 0);
  }
  const blob = await new Promise<Blob>((ok, fail) =>
    canvas.toBlob((b) => (b ? ok(b) : fail(new Error("Foto verwerken lukt niet"))), "image/jpeg", 0.82),
  );
  return { blob, width: w, height: h };
}

/** Pagina's (JPEG) samenvoegen tot één PDF op A4. */
export async function pagesToPdf(pages: { blob: Blob; width: number; height: number }[]): Promise<Blob> {
  const pdf = await PDFDocument.create();
  for (const p of pages) {
    const img = await pdf.embedJpg(await p.blob.arrayBuffer());
    const landscape = p.width > p.height;
    const pw = landscape ? A4.h : A4.w;
    const ph = landscape ? A4.w : A4.h;
    const s = Math.min(pw / p.width, ph / p.height);
    const page = pdf.addPage([pw, ph]);
    page.drawImage(img, {
      x: (pw - p.width * s) / 2,
      y: (ph - p.height * s) / 2,
      width: p.width * s,
      height: p.height * s,
    });
  }
  pdf.setProducer("Klantpunt");
  return new Blob([(await pdf.save()) as Uint8Array<ArrayBuffer>], { type: "application/pdf" });
}

/**
 * Scannen met de telefoon (docs/DOSSIER.md): pagina's fotograferen, als scan bewerken en samenvoegen tot één PDF.
 * Daarna gaat het bestand naar het gewone uploadvenster (soort, titel, geldigheid).
 */
export function ScanDialog({ onClose, onDone }: { onClose: () => void; onDone: (file: File) => void }) {
  const [pages, setPages] = useState<Page[]>([]);
  const [scan, setScan] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const urls = useRef<string[]>([]);
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      const next: Page[] = [];
      for (const f of [...files]) {
        const p = await processPhoto(f, scan);
        const url = URL.createObjectURL(p.blob);
        urls.current.push(url);
        next.push({ ...p, key: crypto.randomUUID(), url });
      }
      setPages((l) => [...l, ...next]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Foto verwerken lukt niet");
    } finally {
      setBusy(false);
    }
  };
  const move = (i: number, d: -1 | 1) =>
    setPages((l) => {
      const n = [...l];
      const [x] = n.splice(i, 1);
      n.splice(i + d, 0, x!);
      return n;
    });
  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      const pdf = await pagesToPdf(pages);
      const stamp = new Date().toLocaleDateString("nl-NL").replace(/\//g, "-");
      onDone(new File([pdf], `Scan ${stamp}.pdf`, { type: "application/pdf" }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "PDF maken lukt niet");
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Scannen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="muted small">
            {pages.length} {pages.length === 1 ? "pagina" : "pagina's"}
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void finish()} disabled={busy || !pages.length}>
            Klaar, verder
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <p className="muted small">
        Leg het papier op een donkere ondergrond en fotografeer pagina voor pagina. De foto&apos;s worden één PDF.
      </p>
      <button
        type="button"
        className="btn primary scan-shot"
        onClick={() => camera.current?.click()}
        disabled={busy}
        data-autofocus
      >
        <IconCamera aria-hidden="true" /> {pages.length ? "Volgende pagina" : "Eerste pagina"}
      </button>
      <input
        ref={camera}
        type="file"
        accept="image/*"
        multiple
        hidden
        data-testid="scan-input"
        onChange={(e) => {
          void add(e.target.files).finally(() => (e.target.value = ""));
        }}
      />
      <label className="check">
        <input type="checkbox" checked={scan} onChange={(e) => setScan(e.target.checked)} /> Als scan (zwart-wit,
        scherper)
      </label>
      {pages.length > 0 && (
        <ol className="scan-pages">
          {pages.map((p, i) => (
            <li key={p.key}>
              <img src={p.url} alt={`Pagina ${i + 1}`} />
              <span className="scan-n num">{i + 1}</span>
              <span className="scan-acts">
                <button
                  type="button"
                  className="btn ghost ico small"
                  aria-label={`Pagina ${i + 1} naar voren`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  <IconChevronLeft size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="btn ghost ico small"
                  aria-label={`Pagina ${i + 1} weghalen`}
                  onClick={() => setPages((l) => l.filter((x) => x.key !== p.key))}
                >
                  <IconTrash size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="btn ghost ico small"
                  aria-label={`Pagina ${i + 1} naar achteren`}
                  disabled={i === pages.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <IconChevronRight size={18} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
